import { useState } from "react";
import { Check, Copy, Loader2, Link2, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useProfileLinks, useCreateProfileLink, useRevokeProfileLink } from "@/hooks/use-profile-links";
import { useUsers } from "@/hooks/use-users";
import { formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import type { ProfileLink } from "@/types";

function typeBadges(link: ProfileLink) {
  const types = [
    link.allowIkev2 && "IKEv2",
    link.allowCisco && "Cisco IPsec",
    link.allowL2tp && "L2TP",
  ].filter(Boolean) as string[];
  return types.map((t) => (
    <Badge key={t} variant="secondary">{t}</Badge>
  ));
}

function linkState(link: ProfileLink): { label: string; className: string } {
  if (link.revoked) return { label: "Revoked", className: "bg-red-100 text-red-800" };
  if (new Date(link.expiresAt).getTime() < Date.now())
    return { label: "Expired", className: "bg-neutral-200 text-neutral-600" };
  if (link.downloadCount >= link.maxDownloads)
    return { label: "Used up", className: "bg-neutral-200 text-neutral-600" };
  return { label: "Active", className: "bg-green-100 text-green-800" };
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline"
      size="icon"
      className="shrink-0"
      aria-label="Copy link"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Failed to copy — select and copy manually");
        }
      }}
    >
      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

export function ProfilesPage() {
  const { data: users } = useUsers();
  const { data: links, isLoading } = useProfileLinks();
  const createLink = useCreateProfileLink();
  const revokeLink = useRevokeProfileLink();

  const [userName, setUserName] = useState("");
  const [allowIkev2, setAllowIkev2] = useState(true);
  const [allowCisco, setAllowCisco] = useState(false);
  const [allowL2tp, setAllowL2tp] = useState(false);
  const [ttlHours, setTtlHours] = useState("48");
  const [maxDownloads, setMaxDownloads] = useState("3");
  const [embedPassword, setEmbedPassword] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userName) {
      toast.error("Select a user.");
      return;
    }
    if (!allowIkev2 && !allowCisco && !allowL2tp) {
      toast.error("Select at least one VPN type.");
      return;
    }
    try {
      const link = await createLink.mutateAsync({
        userName,
        allowIkev2,
        allowCisco,
        allowL2tp,
        ttlHours: Number(ttlHours) || 48,
        maxDownloads: Number(maxDownloads) || 3,
        embedPassword,
      });
      toast.success("Profile link created");
      try {
        await navigator.clipboard.writeText(link.url);
        toast.success("Link copied to clipboard");
      } catch {
        // clipboard unavailable; the URL is shown in the list below
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create link");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Profile links</h1>
        <p className="text-sm text-muted-foreground">
          Shareable per-user links that install ready-to-use VPN profiles on iOS (IKEv2, Cisco IPsec, L2TP).
        </p>
      </div>

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-xl">Create link</CardTitle>
          <CardDescription>The link can be pasted into any messenger and opened on an iPhone.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user">User</Label>
              <select
                id="user"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select user…</option>
                {(users ?? []).filter((u) => u.isEnabled).map((u) => (
                  <option key={u.uid} value={u.uid}>{u.uid}</option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <Label>VPN types</Label>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Switch id="type-ikev2" checked={allowIkev2} onCheckedChange={setAllowIkev2} />
                  <Label htmlFor="type-ikev2" className="font-normal">
                    IKEv2 <span className="text-muted-foreground">(recommended, no shared secret)</span>
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch id="type-cisco" checked={allowCisco} onCheckedChange={setAllowCisco} />
                  <Label htmlFor="type-cisco" className="font-normal">
                    Cisco IPsec <span className="text-muted-foreground">(embeds the group secret)</span>
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch id="type-l2tp" checked={allowL2tp} onCheckedChange={setAllowL2tp} />
                  <Label htmlFor="type-l2tp" className="font-normal">
                    L2TP <span className="text-muted-foreground">(embeds the shared secret)</span>
                  </Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ttl">Expires after (hours)</Label>
                <Input id="ttl" type="number" min={1} max={720} value={ttlHours}
                  onChange={(e) => setTtlHours(e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxdl">Max downloads</Label>
                <Input id="maxdl" type="number" min={1} max={100} value={maxDownloads}
                  onChange={(e) => setMaxDownloads(e.target.value)} className="h-11" />
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Switch id="embed-password" checked={embedPassword} onCheckedChange={setEmbedPassword} />
              <Label htmlFor="embed-password" className="font-normal">
                Embed the user's password
                <span className="block text-xs text-muted-foreground">
                  iOS won't prompt at connect time; the password is stored in plaintext in the downloaded profile.
                </span>
              </Label>
            </div>

            <Button type="submit" className="w-full h-11" disabled={createLink.isPending}>
              {createLink.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create link
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-medium">Existing links</h2>
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {!isLoading && (links ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">No links yet.</p>
        )}
        {(links ?? []).map((link) => {
          const state = linkState(link);
          return (
            <Card key={link.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-medium">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    {link.userName}
                  </div>
                  <Badge className={state.className}>{state.label}</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">{typeBadges(link)}</div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={link.url} className="h-9 font-mono text-xs" onFocus={(e) => e.target.select()} />
                  <CopyButton text={link.url} />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Expires {formatDateTime(link.expiresAt)}</span>
                  <span>{link.downloadCount}/{link.maxDownloads} downloads</span>
                  {!link.revoked && state.label === "Active" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      disabled={revokeLink.isPending}
                      onClick={async () => {
                        try {
                          await revokeLink.mutateAsync(link.id);
                          toast.success("Link revoked");
                        } catch (err) {
                          toast.error(err instanceof Error ? err.message : "Failed to revoke");
                        }
                      }}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> Revoke
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
